/**CORE LIBRARIES IMPORT */
import React, { useEffect, useRef, useState } from "react";

/**
 * Cloudflare Turnstile (Managed) for the booking checkout.
 * The token it produces is single-use and only valid for ~300 s; the server
 * verifies it with Siteverify before reserving any places. Re-render with a
 * new `resetKey` after every submit to get a fresh token.
 */
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
export const TURNSTILE_ACTION = "book-ticket"; // must match the server's expected action
export const ENQUIRY_ACTION = "enquiry"; // submit-enquiry
export const TICKET_RESEND_ACTION = "ticket-resend"; // resend-ticket
let scriptPromise = null;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = SCRIPT_URL;
      s.async = true;
      s.defer = true;
      s.onload = () => resolve(window.turnstile);
      s.onerror = () => {
        scriptPromise = null;
        reject(new Error("turnstile_script_failed"));
      };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

const TurnstileWidget = ({ siteKey, onToken, resetKey, action = TURNSTILE_ACTION, label = "Security check (protects ticket availability from bots):" }) => {
  const container = useRef(null);
  const widgetId = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    onToken(null);
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !container.current) return;
        widgetId.current = turnstile.render(container.current, {
          sitekey: siteKey,
          action,
          theme: "dark",
          appearance: "always",
          "refresh-expired": "auto",
          callback: (token) => onToken(token),
          "expired-callback": () => onToken(null),
          "timeout-callback": () => onToken(null),
          "error-callback": () => {
            onToken(null);
            setError("The security check could not load. Please refresh the page and try again.");
            return true;
          },
        });
      })
      .catch(() => !cancelled && setError("The security check could not load. Please check your connection."));
    return () => {
      cancelled = true;
      if (widgetId.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* ignore */
        }
      }
      widgetId.current = null;
    };
  }, [siteKey, resetKey, action]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="turnstile">
      <p className="details-note">{label}</p>
      <div ref={container} className="turnstile-box" />
      {error && <p className="checkout-error" role="alert">{error}</p>}
    </div>
  );
};

export default TurnstileWidget;
